import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSelector from './LanguageSelector';
import { ThemeToggle } from './theme';
import { Ico } from './icons';
import { getCurrentLanguage } from './i18n';

const KIOSK_KEY_STORAGE = 'nexus_kiosk_key';
const PIN_MAX_LEN = 8;
const PIN_MIN_LEN = 4;
const FEEDBACK_MS = 5000;
const STATUS_POLL_MS = 60000;
const STATUS_RETRY_MS = 15000;
const STATUS_TIMEOUT_MS = 8000;
const CLOCK_TIMEOUT_MS = 10000;
const HID_MAX_GAP_MS = 350;
const HID_FLUSH_MS = 900;

const pad2 = (n) => String(n).padStart(2, '0');

function isTypingTarget(target) {
    if (!target || !target.tagName) return false;
    const tag = target.tagName.toUpperCase();
    return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable === true;
}

async function fetchWithTimeout(url, options = {}, timeoutMs = STATUS_TIMEOUT_MS) {
    const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = ctrl ? setTimeout(() => ctrl.abort(), timeoutMs) : null;
    try {
        return await fetch(url, { ...options, signal: ctrl ? ctrl.signal : undefined });
    } finally {
        if (timer) clearTimeout(timer);
    }
}

function Kiosk() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();

    const [phase, setPhase] = useState('checking');
    const [pairKey, setPairKey] = useState('');
    const [pairError, setPairError] = useState('');
    const [pairBusy, setPairBusy] = useState(false);

    const [status, setStatus] = useState(null);
    const [online, setOnline] = useState(true);
    const [mode, setMode] = useState('nfc');
    const [pin, setPin] = useState('');
    const [busy, setBusy] = useState(false);
    const [feedback, setFeedback] = useState(null);
    const [now, setNow] = useState(() => Date.now());
    const [nfcNative, setNfcNative] = useState(false);

    const keyRef = useRef(localStorage.getItem(KIOSK_KEY_STORAGE) || '');
    const offsetRef = useRef(0);
    const hidBufRef = useRef('');
    const hidLastRef = useRef(0);
    const hidTimerRef = useRef(null);
    const busyRef = useRef(false);
    const feedbackRef = useRef(null);
    const modeRef = useRef('nfc');
    const phaseRef = useRef('checking');
    const ndefRef = useRef(null);

    busyRef.current = busy;
    feedbackRef.current = feedback;
    modeRef.current = mode;
    phaseRef.current = phase;

    const lang = (i18n.language || 'es').slice(0, 2).toLowerCase();

    const applyStatus = useCallback((data) => {
        const serverMs = Date.parse(data.server_time);
        if (!Number.isNaN(serverMs)) {
            offsetRef.current = serverMs - Date.now();
        }
        setStatus(data);
        setOnline(true);
    }, []);

    const fetchStatus = useCallback(async (silent = true) => {
        const key = keyRef.current;
        if (!key) return false;
        try {
            // Cache-buster: evita que un proxy/caché intermedio sirva una respuesta
            // 200 obsoleta/corrupta que haría fallar res.json() con conexión OK.
            const res = await fetchWithTimeout(
                `/api/kiosk/status?_=${Date.now()}`,
                { headers: { 'X-Kiosk-Key': key } },
                STATUS_TIMEOUT_MS,
            );
            if (!res.ok) {
                if (res.status === 401) {
                    localStorage.removeItem(KIOSK_KEY_STORAGE);
                    keyRef.current = '';
                    setPairError(t('kiosk.pair_invalid'));
                    setPhase('pairing');
                }
                setOnline(false);
                return false;
            }
            const data = await res.json();
            applyStatus(data);
            return true;
        } catch (e) {
            if (!silent) setOnline(false);
            else setOnline(false);
            return false;
        }
    }, [applyStatus, t]);

    useEffect(() => {
        let cancelled = false;
        let timerId = null;

        const boot = async () => {
            if (!keyRef.current) {
                setPhase('pairing');
                return;
            }
            await fetchStatus(false);
            if (!cancelled) setPhase('ready');
        };
        boot();

        return () => { cancelled = true; if (timerId) clearTimeout(timerId); };
    }, [fetchStatus]);

    useEffect(() => {
        if (phase !== 'ready') return undefined;
        let stopped = false;
        let id = null;
        let seq = 0;

        const schedule = (ms) => {
            const my = ++seq;
            if (id) clearTimeout(id);
            id = setTimeout(async () => {
                if (stopped || my !== seq) return;
                const ok = await fetchStatus(true);
                if (!stopped && my === seq) schedule(ok ? STATUS_POLL_MS : STATUS_RETRY_MS);
            }, ms);
        };
        // Primer re-chequeo rápido: si el arranque falló, se reintenta en 15s (no 60s).
        schedule(STATUS_RETRY_MS);

        const onVisible = () => {
            if (document.visibilityState === 'visible') schedule(0);
        };
        const onOnline = () => schedule(0); // reconexión del dispositivo → comprobar ya
        document.addEventListener('visibilitychange', onVisible);
        window.addEventListener('online', onOnline);

        return () => {
            stopped = true;
            if (id) clearTimeout(id);
            document.removeEventListener('visibilitychange', onVisible);
            window.removeEventListener('online', onOnline);
        };
    }, [phase, fetchStatus]);

    useEffect(() => {
        const id = setInterval(() => setNow(Date.now() + offsetRef.current), 250);
        return () => clearInterval(id);
    }, []);

    const showFeedback = useCallback((fb) => {
        setFeedback(fb);
    }, []);

    useEffect(() => {
        if (!feedback) return undefined;
        const id = setTimeout(() => {
            setFeedback(null);
            fetchStatus(true);
        }, FEEDBACK_MS);
        return () => clearTimeout(id);
    }, [feedback, fetchStatus]);

    const submitFichaje = useCallback(async (body) => {
        if (busyRef.current || feedbackRef.current) return;
        setBusy(true);
        try {
            const res = await fetchWithTimeout(
                '/api/kiosk/clock',
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Kiosk-Key': keyRef.current,
                    },
                    body: JSON.stringify(body),
                },
                CLOCK_TIMEOUT_MS,
            );
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                showFeedback({ type: 'error', message: data.error || t('common.http_error', { status: res.status }) });
                return;
            }
            const when = data.entry
                ? (data.entry.clock_out ? data.entry.clock_out : data.entry.clock_in)
                : null;
            let hhmm = '';
            if (when) {
                const d = new Date(when);
                if (!Number.isNaN(d.getTime())) hhmm = `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
            }
            showFeedback({
                type: data.action === 'clock_in' ? 'in' : 'out',
                name: data.employee?.full_name || '',
                time: hhmm,
                duplicate: !!data.duplicate,
            });
        } catch (e) {
            setOnline(false);
            showFeedback({ type: 'error', message: t('kiosk.offline') });
        } finally {
            setBusy(false);
            setPin('');
        }
    }, [showFeedback, t]);

    useEffect(() => {
        if (phase !== 'ready' || mode !== 'nfc') return undefined;

        const flush = () => {
            const uid = hidBufRef.current;
            hidBufRef.current = '';
            hidLastRef.current = 0;
            if (uid.length >= 4) submitFichaje({ nfc_uid: uid });
        };

        const onKey = (e) => {
            if (isTypingTarget(e.target)) return;
            if (busyRef.current || feedbackRef.current) return;

            if (e.key === 'Enter') {
                if (hidBufRef.current) {
                    if (hidTimerRef.current) { clearTimeout(hidTimerRef.current); hidTimerRef.current = null; }
                    flush();
                }
                return;
            }
            if (e.key && e.key.length === 1 && /[0-9a-zA-Z]/.test(e.key)) {
                const tNow = Date.now();
                if (tNow - hidLastRef.current > HID_MAX_GAP_MS) hidBufRef.current = '';
                hidLastRef.current = tNow;
                hidBufRef.current += e.key;
                if (hidBufRef.current.length > 40) hidBufRef.current = '';
                if (hidTimerRef.current) clearTimeout(hidTimerRef.current);
                hidTimerRef.current = setTimeout(flush, HID_FLUSH_MS);
            }
        };

        window.addEventListener('keydown', onKey);
        return () => {
            window.removeEventListener('keydown', onKey);
            if (hidTimerRef.current) { clearTimeout(hidTimerRef.current); hidTimerRef.current = null; }
            hidBufRef.current = '';
        };
    }, [phase, mode, submitFichaje]);

    useEffect(() => {
        if (mode !== 'pin') return undefined;

        const onKey = (e) => {
            if (isTypingTarget(e.target)) return;
            if (busyRef.current || feedbackRef.current) return;
            if (e.key && /^[0-9]$/.test(e.key)) {
                setPin((p) => (p.length >= PIN_MAX_LEN ? p : p + e.key));
            } else if (e.key === 'Backspace') {
                setPin((p) => p.slice(0, -1));
            } else if (e.key === 'Enter') {
                setPin((p) => {
                    if (p.length >= PIN_MIN_LEN) submitFichaje({ pin: p });
                    return p;
                });
            } else if (e.key === 'Escape') {
                setPin('');
            }
        };

        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [mode, submitFichaje]);

    useEffect(() => {
        if (!('NDEFReader' in window) || !window.isSecureContext) return undefined;
        let reader = null;
        let stopped = false;
        try {
            reader = new window.NDEFReader();
            ndefRef.current = reader;
            const start = async () => {
                try {
                    await reader.scan();
                    setNfcNative(true);
                    reader.onreading = (event) => {
                        const uid = (event.serialNumber || '').trim();
                        if (uid && !stopped && !busyRef.current && !feedbackRef.current
                            && modeRef.current === 'nfc' && phaseRef.current === 'ready') {
                            submitFichaje({ nfc_uid: uid });
                        }
                    };
                } catch (e) {
                    setNfcNative(false);
                }
            };
            start();
        } catch (e) {
            setNfcNative(false);
        }
        return () => {
            stopped = true;
            ndefRef.current = null;
        };
    }, [submitFichaje]);

    useEffect(() => {
        if (mode === 'pin') {
            const id = setTimeout(() => { if (pin) setPin(''); }, 30000);
            return () => clearTimeout(id);
        }
        return undefined;
    }, [mode, pin]);

    const handlePair = async (e) => {
        e.preventDefault();
        const key = pairKey.trim();
        setPairError('');
        if (!key || key.length > 64) {
            setPairError(t('kiosk.pair_invalid'));
            return;
        }
        setPairBusy(true);
        try {
            const res = await fetchWithTimeout(
                `/api/kiosk/status?_=${Date.now()}`,
                { headers: { 'X-Kiosk-Key': key } },
                STATUS_TIMEOUT_MS,
            );
            if (!res.ok) {
                setPairError(t('kiosk.pair_invalid'));
                return;
            }
            const data = await res.json();
            localStorage.setItem(KIOSK_KEY_STORAGE, key);
            keyRef.current = key;
            applyStatus(data);
            setPhase('ready');
        } catch (err) {
            setPairError(t('kiosk.pair_error'));
        } finally {
            setPairBusy(false);
        }
    };

    const handleExit = () => {
        if (window.confirm(t('kiosk.exit_confirm'))) {
            navigate('/login');
        }
    };

    const d = new Date(now);
    const hh = pad2(d.getHours());
    const mm = pad2(d.getMinutes());
    const ss = pad2(d.getSeconds());

    let dateStr = '';
    try {
        dateStr = new Intl.DateTimeFormat(lang === 'ca' ? 'ca-ES' : lang === 'en' ? 'en-GB' : 'es-ES', {
            weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
        }).format(d);
    } catch (e) {
        dateStr = d.toDateString();
    }

    if (phase === 'checking') {
        return (
            <div className="kiosk-page kiosk-checking">
                <div className="spinner-lg" aria-label={t('common.loading')} />
            </div>
        );
    }

    if (phase === 'pairing') {
        return (
            <div className="kiosk-page kiosk-pairing">
                <div className="kiosk-pair-card">
                    <div className="kiosk-pair-brand">
                        <div className="brand-mark big"><Ico name="logo" size={26} strokeWidth={0} /></div>
                        <span className="kiosk-pair-word">Nexus</span>
                    </div>
                    <h1 className="kiosk-pair-title">{t('kiosk.pair_title')}</h1>
                    <p className="kiosk-pair-desc">{t('kiosk.pair_desc')}</p>
                    <form onSubmit={handlePair}>
                        <div className="field">
                            <label className="field-label" htmlFor="kiosk-key-input">
                                {t('kiosk.pair_key_label')}
                            </label>
                            <div className="control">
                                <span className="control-ico"><Ico name="key" size={17} /></span>
                                <input
                                    id="kiosk-key-input"
                                    className="input pad-ico mono"
                                    type="text"
                                    autoComplete="off"
                                    spellCheck={false}
                                    value={pairKey}
                                    onChange={(e) => setPairKey(e.target.value)}
                                    placeholder={t('kiosk.pair_key_ph')}
                                    autoFocus
                                />
                            </div>
                        </div>
                        {pairError && (
                            <div className="alert alert-error" role="alert">
                                <Ico name="alert" size={17} />
                                <span>{pairError}</span>
                            </div>
                        )}
                        <button type="submit" className="btn btn-primary kiosk-pair-btn" disabled={pairBusy}>
                            {pairBusy
                                ? <><Ico name="refresh" size={16} className="spin" />{t('kiosk.pair_connecting')}</>
                                : <><Ico name="nfc" size={17} />{t('kiosk.pair_connect')}</>}
                        </button>
                    </form>
                    <div className="kiosk-pair-foot">
                        <ThemeToggle />
                        <LanguageSelector />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="kiosk-page">
            {!online && (
                <div className="kiosk-offline" role="alert">
                    <Ico name="alert" size={16} />
                    <span>{t('kiosk.offline')}</span>
                </div>
            )}

            <header className="kiosk-top">
                <div className="kiosk-top-brand">
                    <div className="brand-mark"><Ico name="logo" size={20} strokeWidth={0} /></div>
                    <div className="kiosk-top-meta">
                        <span className="kiosk-top-name">{status?.tenant_name || 'Nexus'}</span>
                        <span className="kiosk-top-tag">
                            <Ico name="clock" size={12} />
                            {t('kiosk.title')}
                        </span>
                    </div>
                </div>
                <div className="kiosk-top-presence" title={t('kiosk.present')}>
                    <Ico name="users" size={15} />
                    <strong>{status ? status.employees_present : '–'}</strong>
                    <span className="muted">/ {status ? status.employees_total : '–'}</span>
                    <span className="kiosk-presence-label">{t('kiosk.present')}</span>
                </div>
                <div className="kiosk-top-controls">
                    <span className={`kiosk-net${online ? '' : ' off'}`} title={online ? t('kiosk.sync') : t('kiosk.offline')}>
                        <Ico name="wifi" size={15} />
                    </span>
                    <LanguageSelector />
                    <ThemeToggle />
                    <button type="button" className="btn-icon" onClick={handleExit} title={t('kiosk.exit')} aria-label={t('kiosk.exit')}>
                        <Ico name="logout" size={16} />
                    </button>
                </div>
            </header>

            <main className="kiosk-main">
                <div className="kiosk-clock" aria-live="off">
                    <div className="kiosk-time">
                        {hh}<span className="kiosk-time-sep">:</span>{mm}
                        <span className="kiosk-sec">{ss}</span>
                    </div>
                    <div className="kiosk-date">{dateStr}</div>
                </div>

                <div className="kiosk-panel">
                    <div className="kiosk-tabs" role="tablist">
                        <button
                            type="button"
                            role="tab"
                            aria-selected={mode === 'nfc'}
                            className={`kiosk-tab${mode === 'nfc' ? ' active' : ''}`}
                            onClick={() => { setMode('nfc'); setPin(''); }}
                        >
                            <Ico name="nfc" size={20} />
                            {t('kiosk.mode_nfc')}
                        </button>
                        <button
                            type="button"
                            role="tab"
                            aria-selected={mode === 'pin'}
                            className={`kiosk-tab${mode === 'pin' ? ' active' : ''}`}
                            onClick={() => { setMode('pin'); setPin(''); }}
                        >
                            <Ico name="keypad" size={19} />
                            {t('kiosk.mode_pin')}
                        </button>
                    </div>

                    {mode === 'nfc' ? (
                        <div className="kiosk-nfc" role="tabpanel">
                            <div className="kiosk-nfc-icon" aria-hidden="true">
                                <span className="nfc-ring r1" />
                                <span className="nfc-ring r2" />
                                <span className="nfc-ring r3" />
                                <Ico name="nfc" size={54} strokeWidth={1.6} />
                            </div>
                            <div className="kiosk-nfc-title">{busy ? t('kiosk.checking') : t('kiosk.nfc_title')}</div>
                            <div className="kiosk-nfc-hint">{t('kiosk.nfc_hint')}</div>
                            <div className="kiosk-nfc-sub">
                                {t('kiosk.nfc_listening')}
                                {nfcNative && <span className="chip-native">· {t('kiosk.nfc_web')}</span>}
                            </div>
                            <button
                                type="button"
                                className="kiosk-alt"
                                onClick={() => { setMode('pin'); setPin(''); }}
                            >
                                <Ico name="keypad" size={15} />
                                {t('kiosk.use_pin_fallback')}
                            </button>
                        </div>
                    ) : (
                        <div className="kiosk-pin" role="tabpanel">
                            <div className="kiosk-pin-title">{t('kiosk.pin_title')}</div>
                            <div className="kiosk-pin-display" aria-label={t('kiosk.pin_hint')}>
                                {Array.from({ length: PIN_MAX_LEN }).map((_, i) => (
                                    <span key={i} className={`pin-dot${i < pin.length ? ' filled' : ''}${busy && i === pin.length - 1 ? ' checking' : ''}`} />
                                ))}
                                <span className="pin-count">{pin.length}/{PIN_MAX_LEN}</span>
                            </div>
                            <div className="kiosk-keypad" onDoubleClick={(e) => e.preventDefault()}>
                                {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((n) => (
                                    <button
                                        key={n}
                                        type="button"
                                        className="kiosk-key"
                                        onClick={() => setPin((p) => (p.length >= PIN_MAX_LEN ? p : p + n))}
                                        aria-label={n}
                                    >
                                        {n}
                                    </button>
                                ))}
                                <button
                                    type="button"
                                    className="kiosk-key aux"
                                    onClick={() => setPin((p) => p.slice(0, -1))}
                                    aria-label={t('common.delete')}
                                    title={t('common.delete')}
                                >
                                    <Ico name="backspace" size={24} />
                                </button>
                                <button
                                    type="button"
                                    className="kiosk-key"
                                    onClick={() => setPin((p) => (p.length >= PIN_MAX_LEN ? p : p + '0'))}
                                    aria-label="0"
                                >
                                    0
                                </button>
                                <button
                                    type="button"
                                    className="kiosk-key ok"
                                    disabled={pin.length < PIN_MIN_LEN || busy}
                                    onClick={() => submitFichaje({ pin })}
                                >
                                    {busy
                                        ? <Ico name="refresh" size={22} className="spin" />
                                        : <Ico name="check" size={26} strokeWidth={2.4} />}
                                </button>
                            </div>
                            <button
                                type="button"
                                className="kiosk-alt"
                                onClick={() => { setMode('nfc'); setPin(''); }}
                            >
                                <Ico name="nfc" size={15} />
                                {t('kiosk.use_nfc_fallback')}
                            </button>
                        </div>
                    )}
                </div>
            </main>

            <footer className="kiosk-foot">
                <span className="kiosk-foot-brand">
                    <Ico name="logo" size={12} strokeWidth={0} />
                    Nexus · {t('kiosk.title')}
                </span>
                <span className="muted">{online ? t('kiosk.sync') : t('kiosk.offline')}</span>
            </footer>

            {feedback && (
                <div
                    className={`kiosk-feedback${feedback.type === 'error' ? ' error' : ''}`}
                    role="status"
                    onClick={() => setFeedback(null)}
                >
                    <div className="kiosk-fb-card" onClick={(e) => e.stopPropagation()}>
                        {feedback.type === 'error' ? (
                            <div className="kiosk-fb-ico err"><Ico name="alert" size={44} strokeWidth={1.7} /></div>
                        ) : (
                            <div className={`kiosk-fb-ico ${feedback.type === 'in' ? 'in' : 'out'}`}>
                                <Ico name={feedback.type === 'in' ? 'login' : 'logout'} size={44} strokeWidth={1.7} />
                            </div>
                        )}
                        {feedback.type !== 'error' && (
                            <>
                                <div className="kiosk-fb-name">{feedback.name}</div>
                                <div className="kiosk-fb-action">
                                    {feedback.type === 'in' ? t('kiosk.clocked_in') : t('kiosk.clocked_out')}
                                    {feedback.time && <> · {feedback.time}</>}
                                </div>
                                {feedback.duplicate && (
                                    <div className="kiosk-fb-dup">{t('kiosk.duplicate')}</div>
                                )}
                            </>
                        )}
                        {feedback.type === 'error' && (
                            <div className="kiosk-fb-msg">{feedback.message}</div>
                        )}
                        <div className="kiosk-fb-hint">{t('kiosk.fb_hint')}</div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Kiosk;
